import { axiosClientAdmin } from '@/lib/auth';
import { UseQueryOptions } from '@tanstack/react-query';
import { Comparator, FilterOperator, MetricGranularity, SortOrder } from '@/lib/api/types.gen';
import { SearchInput } from '@/lib/api/util/types';

// --- Manifest types ---

export interface ManifestGroup {
  key: string;
  displayName: string;
  order: number;
}

export interface ManifestStage {
  key: string;
  order: number;
  displayName: string;
  description?: string;
  visibility: 'public' | 'internal' | 'erroronly';
  group?: string;
  dependsOn: string[];
  knownTypes: string[];
}

export interface ManifestOutcome {
  key: string;
  displayName: string;
}

export interface IngestionManifest {
  groups: ManifestGroup[];
  stages: ManifestStage[];
  outcomes: ManifestOutcome[];
}

// --- Types ---

export interface IngestionFilter {
  projectId?: string;
  videoProcessorConfigId?: string;
  assetId?: string;
  stage?: string;
  outcome?: string;
  type?: string;
}

export interface IngestionEvent {
  timestamp: string;
  stage: string;
  outcome: string;
  correlationId?: string;
  videoProcessorConfigId?: string;
  projectId?: string;
  assetId?: string;
  monitoringEventId?: string;
  tenantId?: string;
  type?: string;
  stopReason?: string;
  reason?: string;
  metadataError?: string;
  metadataEventType?: string;
  metadataPredictedStatus?: string;
  metadataVendor?: string;
  metadataTriggerType?: string;
  // Enriched fields (populated from fact table, null from staging view)
  deviceName?: string;
  deviceBrand?: string;
  videoProcessorType?: string;
  projectName?: string;
  companyName?: string;
  companyId?: string;
}

export interface DateRangeInput {
  dateStart: string;
  dateEnd: string;
}

export type Granularity = 'HOUR' | 'DAY' | 'WEEK';

/** Compute chart granularity based on date range span. */
export function computeGranularity(dateRange: DateRangeInput): Granularity {
  const ms = new Date(dateRange.dateEnd).getTime() - new Date(dateRange.dateStart).getTime();
  const hours = ms / (1000 * 60 * 60);
  if (hours <= 72) return 'HOUR';
  const days = hours / 24;
  if (days <= 30) return 'DAY';
  return 'WEEK';
}

/** Convert local Granularity to MetricGranularity enum for the metrics API. */
export function toMetricGranularity(g: Granularity): MetricGranularity {
  switch (g) {
    case 'HOUR': return MetricGranularity.Hour;
    case 'DAY': return MetricGranularity.Day;
    case 'WEEK': return MetricGranularity.Week;
  }
}

// --- Journey types ---

export interface JourneyStage {
  stage: string;
  outcome: string;
  timestamp: string;
  type?: string;
  stopReason?: string;
}

export interface IngestionJourney {
  correlationId: string;
  firstEventAt: string;
  lastEventAt: string;
  durationMs: number;
  stageCount: number;
  stages: JourneyStage[];
  finalOutcome: string;
  finalStage: string;
  entryStage: string;
  videoProcessorConfigId?: string;
  projectId?: string;
  companyId?: string;
  monitoringEventId?: string;
  deviceName?: string;
  deviceBrand?: string;
  videoProcessorType?: string;
  projectName?: string;
  companyName?: string;
  firstError?: string;
  firstReason?: string;
  firstStopReason?: string;
  hasFailure: boolean;
  hasError: boolean;
  wasFiltered: boolean;
  vendor?: string;
  triggerType?: string;
}

export interface IngestionJourneySearchResult {
  journeys: IngestionJourney[];
  totalCount: number;
}

export interface JourneySearchInput extends DateRangeInput {
  finalOutcome?: string;
  projectId?: string;
  videoProcessorConfigId?: string;
  companyId?: string;
  pageSize?: number;
  page?: number;
}

export interface JourneyHistogramBucket {
  bucket: string;
  finalOutcome: string;
  count: number;
}

// --- Project search (for filter combobox) ---

export interface ProjectSearchRow {
  id: string;
  name: string;
  companyName: string;
}

export async function searchProjects(params: {
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
}) {
  const search = new SearchInput({
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    sortBy: 'name',
    sortDir: SortOrder.Ascending,
  });

  if (params.searchTerm) {
    search.filterFilterGroups.push({
      operator: FilterOperator.Or,
      filters: [
        { field: 'name', action: Comparator.ILike, value: params.searchTerm },
      ],
    });
  }

  const { data } = await axiosClientAdmin.get<{ rows: ProjectSearchRow[]; totalRows?: number }>(
    '/Monitoring/projects/search',
    { params: search.toParams() }
  );

  return {
    rows: data.rows ?? [],
    totalCount: data.totalRows ?? 0,
    hasNextPage: (data.rows?.length ?? 0) === params.pageSize,
  };
}

// --- Real-time query builders (tenant-scoped) ---

export function FetchEventTraceQuery(
  tenantId: string,
  correlationId: string,
  timestampHint?: string
): UseQueryOptions<IngestionEvent[]> {
  return {
    queryKey: ['ingestion', 'trace', tenantId, correlationId],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.get<IngestionEvent[]>(
        `/tenants/${tenantId}/ingestion/trace/${correlationId}`,
        { params: timestampHint ? { timestampHint } : undefined }
      );
      return data;
    },
    staleTime: 60_000,
    enabled: !!correlationId && !!tenantId,
  };
}

// --- Journey query builders ---

export function FetchJourneysInfiniteQuery(
  tenantId: string,
  input: Omit<JourneySearchInput, 'page' | 'pageSize'>,
  pageSize = 50
) {
  return {
    queryKey: ['ingestion', 'journeys-infinite', tenantId, input, pageSize] as const,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const { data } = await axiosClientAdmin.get<IngestionJourneySearchResult>(
        `/tenants/${tenantId}/ingestion/journeys`,
        { params: { ...input, page: pageParam, pageSize } }
      );
      return data;
    },
    initialPageParam: 1 as number,
    getNextPageParam: (lastPage: IngestionJourneySearchResult, allPages: IngestionJourneySearchResult[]) => {
      const totalLoaded = allPages.reduce((sum: number, p: IngestionJourneySearchResult) => sum + p.journeys.length, 0);
      return totalLoaded < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
  };
}

export function FetchJourneyHistogramQuery(
  tenantId: string,
  dateRange: DateRangeInput
): UseQueryOptions<JourneyHistogramBucket[]> {
  return {
    queryKey: ['ingestion', 'journey-histogram', tenantId, dateRange],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.get<JourneyHistogramBucket[]>(
        `/tenants/${tenantId}/ingestion/journeys/histogram`,
        { params: dateRange }
      );
      return data;
    },
    staleTime: 30_000,
    enabled: !!tenantId,
  };
}

// --- Raw logs query (Cloud Logging direct) ---

export interface RawLogSearchInput {
  since?: string;
  until?: string;
  stage?: string;
  outcome?: string;
  correlationId?: string;
  type?: string;
  projectId?: string;
  videoProcessorConfigId?: string;
}

export function FetchRawLogsQuery(
  tenantId: string,
  input: RawLogSearchInput
): UseQueryOptions<IngestionEvent[]> {
  return {
    queryKey: ['ingestion', 'raw-logs', tenantId, input],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.get<IngestionEvent[]>(
        `/tenants/${tenantId}/ingestion/logs/raw`,
        { params: input }
      );
      return data;
    },
    staleTime: 0,
    enabled: !!tenantId,
  };
}

// --- Manifest query ---

export function FetchManifestQuery(
  tenantId: string
): UseQueryOptions<IngestionManifest> {
  return {
    queryKey: ['ingestion', 'manifest', tenantId],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.get<IngestionManifest>(
        `/tenants/${tenantId}/ingestion/manifest`
      );
      return data;
    },
    staleTime: 3_600_000, // 1 hour, matching backend cache
    enabled: !!tenantId,
  };
}
